export const hexToRgb = (hex: string) => {
    const clean = hex.replace("#", "");
    const full =
        clean.length === 3
            ? clean
                  .split("")
                  .map((c) => c + c)
                  .join("")
            : clean;
    const bigint = parseInt(full, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return { r, g, b };
};

export const rgbToLinear = (value: number) => {
    const c = value / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

export const linearToRgb = (value: number) => {
    const c =
        value <= 0.0031308 ? 12.92 * value : 1.055 * value ** (1 / 2.4) - 0.055;
    return Math.round(Math.min(1, Math.max(0, c)) * 255);
};

export const rgbToHex = (acc: string, v: number) => {
    acc += v.toString(16).padStart(2, "0");
    return acc;
};

export const blendColorsLinear = (colors: string[]) => {
    // https://www.w3.org/TR/css-color-4/#predefined-sRGB
    if (!colors.length) return undefined;

    let sumR = 0,
        sumG = 0,
        sumB = 0;

    for (const hex of colors) {
        const { r, g, b } = hexToRgb(hex);

        sumR += rgbToLinear(r);
        sumG += rgbToLinear(g);
        sumB += rgbToLinear(b);
    }

    const n = colors.length;
    return [sumR / n, sumG / n, sumB / n]
        .map(linearToRgb)
        .reduce(rgbToHex, "#");
};

export const blendColors = (colors: string[]) => {
    if (!colors.length) return undefined;

    return colors
        .reduce(
            (acc, hex) => {
                const { r, g, b } = hexToRgb(hex);
                return [acc[0] + r, acc[1] + g, acc[2] + b];
            },
            [0, 0, 0]
        )
        .map((v) => Math.round(v / colors.length))
        .reduce(rgbToHex, "#");
};

const hexToLuminance = (r: number, g: number, b: number) => {
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;

    return l;
};

export const sortColorsByLuminance = <T>(
    colors: T[],
    map: (color: T) => string
) => {
    return colors.sort((a, b) => {
        const { r: ar, g: ag, b: ab } = hexToRgb(map(a));
        const { r: br, g: bg, b: bb } = hexToRgb(map(b));

        return hexToLuminance(br, bg, bb) - hexToLuminance(ar, ag, ab);
    });
};

export const getColorRange = (colors: string[], n: number) => {
    const sortedColors = sortColorsByLuminance(colors, (c) => c);
    if (sortedColors.length <= n) return sortedColors;

    const step = sortedColors.length / n;
    const result = [];
    for (let i = 0; i < n; i++) {
        result.push(sortedColors[Math.floor(i * step)]);
    }
    return result;
};

export const isDarkColor = (hexColor: string) => {
    const { r, g, b } = hexToRgb(hexColor);
    const luminance = hexToLuminance(r, g, b);
    return luminance < 128;
};
